import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedPayment } from 'tradera/state/views/invoice-checkout/reducer';
import { LocalLoader } from 'tradera/components/loader/loader';
import { useCurrentLanguage, useTranslator } from 'tradera/lang/translate';
import { PAYMENT_OPTIONS } from 'tradera/state/views/invoice-checkout/initial-state';
import { selectInvoiceCheckout } from 'tradera/state/views/invoice-checkout/selector';
import PaymentMethodLogo from 'tradera/components/payment-method/payment-method-logo';
import api from 'tradera/state/views/invoice-checkout/api';
import qs from 'qs';
import { useFetchData } from 'tradera/hooks/use-fetch-data';

import './braintree-drop-in.scss';
import { Button } from 'tradera/components/atoms/button/button';
import { PaymentMethodName } from 'tradera/components/payment-method/payment-method-name';

// Testing card number for dev environment: 4111111111111111

export var paymentInstance = null;

const BraintreeDropIn = ({
    paymentOptions,
    selected,
    onPaymentMethodRequestable
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const isNativeAppWithApplyPaySupport = useSelector(
        (state) => state.environment.isNativeAppWithApplyPaySupport
    );

    const preselectedPaymentOption = qs.parse(window.location.search, {
        ignoreQueryPrefix: true
    })['payment-option'];

    useEffect(() => {
        if (!preselectedPaymentOption) return;

        dispatch(setSelectedPayment(preselectedPaymentOption));
    }, [preselectedPaymentOption, dispatch]);

    const { paymentOption } = paymentOptions;

    const handlePaymentSelected = () => {
        const url = new URL(window.location.href);
        if (!selected) {
            if (isNativeAppWithApplyPaySupport) {
                url.searchParams.set('payment-option', paymentOption);
                return (window.location = url.toString());
            }
            dispatch(setSelectedPayment(paymentOption));
        }
    };

    return (
        <>
            <div className="form-check form-check-sm align-items-start mb-0">
                <input
                    className="form-check-input py-2"
                    type="radio"
                    id={paymentOption}
                    checked={selected}
                    onChange={handlePaymentSelected}
                />
                <label
                    className="form-check-label ml-1 w-100 py-2"
                    htmlFor={paymentOption}>
                    <div className="d-flex justify-content-between text-inter-medium">
                        <PaymentMethodName paymentMethod={paymentOption} />
                        <PaymentMethodLogo paymentMethod={paymentOption} />
                    </div>
                    <div className="text-gray-600 mt-1">
                        {paymentOption === PAYMENT_OPTIONS.VISA_MASTERCARD &&
                            t('payment_method_info_visaMasterCard')}
                        {paymentOption === PAYMENT_OPTIONS.APPLEPAY &&
                            t('payment_method_info_applePay')}
                        {paymentOption === PAYMENT_OPTIONS.GOOGLEPAY &&
                            t('payment_method_info_googlePay')}
                    </div>
                </label>
            </div>
            {selected && (
                <div className="pl-md-5">
                    <DropInSelected
                        paymentOption={paymentOption}
                        onPaymentMethodRequestable={onPaymentMethodRequestable}
                    />
                </div>
            )}
        </>
    );
};

const DropInSelected = ({ paymentOption, onPaymentMethodRequestable }) => {
    const { language } = useCurrentLanguage();
    const { t } = useTranslator();
    const { invoiceId, amountOwed: totalCost } = useSelector(
        selectInvoiceCheckout
    );

    const fetchParams = useMemo(() => {
        return {
            invoiceId: invoiceId,
            paymentOption: paymentOption
        };
    }, [invoiceId, paymentOption]);

    //Fetch Braintree Token
    const { data, isLoading, isRejected, isResolved, retryFetch } =
        useFetchData(
            api.getBraintreeToken,
            fetchParams,
            'Invoice Checkout Braintree token'
        );

    useEffect(() => {
        const dropIn = async () => {
            if (isResolved && !paymentInstance) {
                const { clientToken } = data;
                await initDropIn(
                    clientToken,
                    language,
                    paymentOption,
                    totalCost,
                    onPaymentMethodRequestable
                );
            }
        };
        dropIn();
    }, [
        data,
        isResolved,
        language,
        paymentOption,
        totalCost,
        onPaymentMethodRequestable
    ]);

    // Update amount in configuration for Apple Pay, Google Pay
    useEffect(() => {
        let paymentProvider = null;
        if (paymentInstance) {
            switch (paymentOption) {
                case PAYMENT_OPTIONS.APPLEPAY:
                    paymentProvider = 'applepay';
                    break;
                case PAYMENT_OPTIONS.GOOGLEPAY:
                    paymentProvider = 'googlepay';
                    break;
                default:
                    paymentProvider = null;
                    break;
            }
        }
        if (paymentProvider) {
            paymentInstance.updateConfiguration(
                paymentProvider,
                'amount',
                totalCost
            );
        }
    }, [paymentOption, totalCost]);

    // Effect to reset paymentIntance when switching payment option
    useEffect(() => {
        return () => {
            // eslint-disable-next-line better-mutation/no-mutation
            paymentInstance = null; // Reset paymentInstance when unmounting
        };
    }, [paymentOption]);

    return (
        <div className="position-relative">
            {isRejected && (
                <div className="d-flex flex-column py-2 justify-content-center">
                    <div className="my-3 text-center">
                        <h3>{t('error_somethingWentWrong')}</h3>
                    </div>
                    <Button
                        theme="action"
                        variant="outline"
                        className="mx-auto"
                        onClick={() => retryFetch()}>
                        {t('error_reloadPage')}
                    </Button>
                </div>
            )}
            {isLoading && (
                <div
                    style={{
                        minHeight: '135px'
                    }}>
                    <LocalLoader />
                </div>
            )}
            <div
                className={`braintree-drop-in ${
                    isLoading ? 'bg-light' : 'bg-white'
                }`}>
                <div id="dropin-container" />
            </div>
        </div>
    );
};

const initDropIn = async (
    clientToken,
    language,
    paymentOption,
    totalCost,
    onPaymentMethodRequestable
) => {
    const callback = (err, dropinInstance) => {
        if (err) {
            return null;
        }

        if (dropinInstance.isPaymentMethodRequestable()) {
            onPaymentMethodRequestable(null, true);
        }

        dropinInstance.on(
            'paymentMethodRequestable',
            function (paymentMethodRequestable) {
                onPaymentMethodRequestable(paymentMethodRequestable, true);
            }
        );

        dropinInstance.on('noPaymentMethodRequestable', function () {
            onPaymentMethodRequestable(null, false);
        });

        // eslint-disable-next-line better-mutation/no-mutation
        paymentInstance = dropinInstance;
    };

    const paymentOptions = getDropInOptions(paymentOption, totalCost);
    var options = {
        authorization: clientToken,
        container: '#dropin-container',
        locale: language,
        vaultManager: true,
        dataCollector: {
            kount: true // Required if Kount fraud data collection is enabled
        },
        card: {
            vault: {
                allowVaultCardOverride: true,
                vaultCard: false
            }
        },
        ...paymentOptions
    };

    const dropin = await import(
        /* webpackChunkName: "braintree-web-drop-in" */ 'braintree-web-drop-in'
    );
    await dropin.create(options, callback);
};

const getDropInOptions = (paymentOption, totalCost) => {
    switch (paymentOption) {
        case PAYMENT_OPTIONS.VISA_MASTERCARD:
            return {
                threeDSecure: true
            };
        case PAYMENT_OPTIONS.APPLEPAY:
            return {
                card: false,
                paypal: false,
                googlePay: false,
                applePay: {
                    displayName: 'Tradera',
                    paymentRequest: {
                        total: {
                            label: 'Tradera',
                            amount: totalCost.toString()
                        },
                        requiredBillingContactFields: ['postalAddress']
                    }
                }
            };
        case PAYMENT_OPTIONS.GOOGLEPAY:
            return {
                card: false,
                paypal: false,
                applePay: false,
                threeDSecure: true,
                googlePay: {
                    googlePayVersion: 2,
                    merchantId: 'BCR2DN6TU7KPRDTQ',
                    transactionInfo: {
                        totalPriceStatus: 'FINAL',
                        totalPrice: totalCost.toString(),
                        currencyCode: 'SEK'
                    },
                    allowedPaymentMethods: [
                        {
                            type: 'CARD',
                            parameters: {
                                billingAddressRequired: true,
                                billingAddressParameters: {
                                    format: 'FULL'
                                }
                            }
                        }
                    ]
                }
            };
        default:
            return {};
    }
};

export default BraintreeDropIn;
